import classNames from 'clsx';
import { addParams } from 'lib/urlUtils';
import React, { type ReactElement, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { authPopup } from 'reactApp/modules/ph/ph.thunkActions';
import type { NormalizedTariffByProductIds } from 'reactApp/modules/products/products.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { normalizeMobileTariffList } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.helpers';
import { TariffCard as MobileTariffCard } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard';
import { MobileTariffCardTheme } from 'reactApp/sections/MobileSubscriptionsPage/TariffCard/TariffCard.types';
import { sendNestleLandingAnalytics, sendNestleLandingTmrGoal } from 'reactApp/sections/Nestle/NestleLanding.analytics';
import { defaultTariffFeature, familyTariffFeature } from 'reactApp/sections/Nestle/TariffsBlock/TariffCard.data';
import { ETariffLabelKind } from 'reactApp/ui/TariffCardNew/components/TariffLabel.types';
import { TariffCard } from 'reactApp/ui/TariffCardNew/TariffCard';
import { ETariffCardSizeButtonSize, ETariffCardWidth, TariffCardContentSize } from 'reactApp/ui/TariffCardNew/TariffCard.types';
import { useYearSaving } from 'reactApp/ui/TariffSectionNew/hooks/useYearSaving';
import { noopVoid } from 'reactApp/utils/helpers';
import { formatPrice, getMonthProductPrice } from 'reactApp/utils/priceHelpers';

import styles from './TariffsBlock.css';

const EVENT_CLICK = 'click-tariff';

export const TariffNestleCard = memo(
    ({
        tariff,
        isYear,
        isMobile,
        onSuccessPayment,
    }: {
        tariff: NormalizedTariffByProductIds;
        isYear: boolean;
        isMobile?: boolean;
        onSuccessPayment?: () => void;
    }): ReactElement => {
        const dispatch = useDispatch();

        const isAnonymous = useSelector(UserSelectors.isAnonymous);

        const { space, tariffId, products } = tariff;

        const mobileTariff = normalizeMobileTariffList(
            {
                [tariffId]: [products.yearProduct, products.monthProduct],
            },
            true
        );

        const [_, yearSavingDiscount] = useYearSaving(products);

        const buttonText = useMemo(
            () => `${formatPrice(getMonthProductPrice(isYear ? products.yearProduct : products.monthProduct))} ₽ месяц`,
            []
        );
        const buttonAdditionalText = useMemo(() => (isYear ? 'при покупке на год' : ''), [isYear]);

        const label = useMemo(() => {
            const product = products.yearProduct;

            if (product.isFamily) {
                return 'Для семьи';
            }

            return 'Для себя';
        }, [products]);

        const openBuyWindow = useCallback(
            (productId) => {
                if (isAnonymous) {
                    sendNestleLandingTmrGoal('login-show');

                    dispatch(
                        authPopup({
                            closable: true,
                            loginRequest: true,
                            successPage: addParams(window.location.href, {
                                tariffs: true,
                                success: true,
                            }),
                        })
                    );
                    return;
                }

                dispatch(
                    openTariffBuy({
                        productId,
                        isMobile,
                        paySource: getPaySource(`nestle`),
                        onSuccess: () => {
                            sendNestleLandingTmrGoal('buy-success', { params: productId });
                            onSuccessPayment?.();
                        },
                    })
                );
            },
            [isAnonymous, onSuccessPayment, isMobile]
        );

        const onBuy = useCallback(() => {
            const productId = isYear ? products.yearProduct.id : products?.monthProduct?.id || products.yearProduct.id;

            sendNestleLandingTmrGoal(EVENT_CLICK, { params: productId });
            sendNestleLandingAnalytics({
                eventCategory: 'click',
                action: EVENT_CLICK,
                productId,
            });

            openBuyWindow(productId);
        }, [isYear, products, openBuyWindow]);

        const onMobileBuy = useCallback(
            ({ selectedProduct }) => {
                sendNestleLandingTmrGoal(EVENT_CLICK, { params: selectedProduct.id });
                sendNestleLandingAnalytics({
                    eventCategory: 'click',
                    action: EVENT_CLICK,
                    productId: selectedProduct.id,
                });

                openBuyWindow(selectedProduct.id);
            },
            [openBuyWindow]
        );

        if (isMobile) {
            return (
                <div className={classNames(styles.mobileCard, [styles[`mobileCard-${space.space}`]])}>
                    <MobileTariffCard
                        {...(mobileTariff[0] as any)}
                        onClick={onMobileBuy}
                        theme={MobileTariffCardTheme.white}
                        showPriceByMonth
                        skipBenefitsFeature
                        isRebrandingQuotaLanding
                        customLabel={label}
                        isFamily={false}
                        isSingleQuota={false}
                        featuresList={products.yearProduct.isFamily ? familyTariffFeature : defaultTariffFeature}
                    />
                </div>
            );
        }

        return (
            <div className={classNames(styles.card, [styles[`card-${space.space}`]])}>
                <TariffCard
                    onClick={noopVoid}
                    dataQa="QuotaNestleTariffCard"
                    tariffId={tariffId}
                    width={ETariffCardWidth.fluid}
                    space={{
                        space: space.space,
                        units: space.units,
                    }}
                    isRebrandingQuotaLanding
                    compact
                    buttonSize={ETariffCardSizeButtonSize.big}
                    buttonText={buttonText}
                    buttonPrimaryLight
                    buttonAdditionalText={buttonAdditionalText}
                    onClickButton={onBuy}
                    label={label}
                    labelKind={products.yearProduct.isFamily ? ETariffLabelKind.tariffPromo : ETariffLabelKind.green}
                    buttonInFooter
                    buttonDark
                    savingInButton={isYear ? yearSavingDiscount : undefined}
                    featureListOptions={products.yearProduct.isFamily ? familyTariffFeature : defaultTariffFeature}
                    compactFont
                    buttonInFooterGap={0}
                    contentSize={TariffCardContentSize.compact}
                />
            </div>
        );
    }
);

TariffNestleCard.displayName = 'TariffNestleCard';
